import { useRef, useEffect } from "react";
import { Bold, Italic, Heading2, Heading3, List, Link2, Quote } from "lucide-react";
import Uploader from "@/components/Uploader";

export default function RichTextEditor({ value, onChange }) {
  const ref = useRef(null);
  const savedRange = useRef(null);

  useEffect(() => {
    if (ref.current) ref.current.innerHTML = value || "";
    // init once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = () => onChange(ref.current?.innerHTML || "");

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && ref.current && ref.current.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const restoreSelection = () => {
    const sel = window.getSelection();
    ref.current?.focus();
    if (savedRange.current) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
  };

  const exec = (cmd, val = null) => {
    restoreSelection();
    document.execCommand(cmd, false, val);
    emit();
    saveSelection();
  };

  const block = (tag) => exec("formatBlock", tag);

  const addLink = () => {
    const url = window.prompt("Link URL (https://…)");
    if (url) exec("createLink", url);
  };

  const insertImage = (url) => {
    restoreSelection();
    document.execCommand("insertImage", false, url);
    emit();
    saveSelection();
  };

  const Btn = ({ onClick, icon: Icon, title, tid }) => (
    <button
      type="button"
      title={title}
      data-testid={tid}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-white/70 transition-colors hover:border-gold hover:text-gold-light"
    >
      <Icon size={14} />
    </button>
  );

  return (
    <div className="rounded-lg border border-white/10 bg-ink-900">
      <div className="flex flex-wrap items-center gap-1.5 border-b border-white/10 p-2">
        <Btn onClick={() => exec("bold")} icon={Bold} title="Bold" tid="rte-bold" />
        <Btn onClick={() => exec("italic")} icon={Italic} title="Italic" tid="rte-italic" />
        <Btn onClick={() => block("<h2>")} icon={Heading2} title="Heading 2" tid="rte-h2" />
        <Btn onClick={() => block("<h3>")} icon={Heading3} title="Heading 3" tid="rte-h3" />
        <Btn onClick={() => block("<blockquote>")} icon={Quote} title="Quote" tid="rte-quote" />
        <Btn onClick={() => exec("insertUnorderedList")} icon={List} title="Bullet list" tid="rte-list" />
        <Btn onClick={addLink} icon={Link2} title="Insert link" tid="rte-link" />
        <span className="ml-1" onMouseDown={(e) => e.preventDefault()}>
          <Uploader accept="image/*" label="Image" testid="rte-image-upload" onUploaded={insertImage} />
        </span>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={() => { emit(); saveSelection(); }}
        onKeyUp={saveSelection}
        onMouseUp={saveSelection}
        data-testid="rte-body"
        className="article-content min-h-[220px] px-4 py-3 font-body text-sm text-white/85 outline-none"
      />
    </div>
  );
}
